import React, { useState } from 'react'
import Papa from 'papaparse'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import { reconciliationBXService } from 'BoostXpressAdmin/Services/reconciliationBXService'
import {
  ReconciliationItem,
  ReconciliationStatus,
} from 'BoostXpressAdmin/Model/reconciliationXModel'
import { useDebouncedValue, useStationOperationsPage } from '../hooks/useStationOperationsList'
import {
  formatStationLitres,
  stationOperationsError,
} from 'BoostXpressAdmin/Utils/stationOperations'
import './Reconciliation.scss'

const Reconciliation: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const deferredStationId = useDebouncedValue(searchTerm.trim())
  const [productFilter, setProductFilter] = useState<'all' | 'PMS' | 'AGO'>('all')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [page, setPage] = useState(1)
  const limit = 10

  const {
    items: items,
    total,
    isLoading,
    isError,
    error,
    refetch,
  } = useStationOperationsPage<
    ReconciliationItem,
    { stationId?: string; product?: 'PMS' | 'AGO' }
  >(
    'reconciliation',
    reconciliationBXService.list,
    {
      stationId: deferredStationId || undefined,
      product: productFilter === 'all' ? undefined : productFilter,
    },
    page,
    limit,
  )
  const exportCsv = () => {
    if (!items.length) return
    const csv = Papa.unparse(
      items.map((item) => ({
        Station: item.station,
        Product: item.product,
        'Report Date': item.reportDate,
        'Pump Sales': `${item.pumpSales.toLocaleString()} L`,
        'Tank Movement': `${item.tankMovement.toLocaleString()} L`,
        Variance: formatStationLitres(item.variance),
        Status: item.status,
      })),
    )
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reconciliation_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const renderVariance = (val: number | null) => {
    if (val === null) return <span>Not available</span>
    if (val > 0) {
      return (
        <span className='bx-rec-val--positive'>
          {val.toLocaleString('en-NG', { maximumFractionDigits: 2 })} L
        </span>
      )
    }
    if (val < 0) {
      return (
        <span className='bx-rec-val--negative'>
          {val.toLocaleString('en-NG', { maximumFractionDigits: 2 })} L
        </span>
      )
    }
    return <span className='bx-rec-val--neutral'>0.0 L</span>
  }

  const renderStatusBadge = (status: ReconciliationStatus) => {
    switch (status) {
      case 'Balanced':
        return <span className='bx-rec-pill bx-rec-pill--balanced'>Balanced</span>
      case 'Excess':
        return <span className='bx-rec-pill bx-rec-pill--excess'>Excess</span>
      case 'Deficit':
        return <span className='bx-rec-pill bx-rec-pill--deficit'>Deficit</span>
      default:
        return <span className='bx-rec-pill'>{status}</span>
    }
  }

  const totalPages = Math.ceil(total / limit) || 1

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    if (page <= 3) {
      return [1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages]
    }
    if (page >= totalPages - 2) {
      return [1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages]
    }
    return [1, '...', page - 1, page, page + 1, '...', totalPages]
  }

  return (
    <div className='bx-reconciliation-page'>
      {isError && (
        <div className='alert alert-danger' role='alert'>
          {stationOperationsError(error)}{' '}
          <button type='button' onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}
      {/* Header section matching Figma */}
      <div className='bx-reconciliation-page__header'>
        <h2 className='bx-reconciliation-page__title'>Reconciliation</h2>
        <p className='bx-reconciliation-page__subtitle'>
          Volume variance by product across station reports. Left is negative, right is positive,
          center is balanced.
        </p>
      </div>

      {/* Controls Bar: full-width search and action buttons */}
      <div className='bx-reconciliation-page__controls'>
        <div className='bx-reconciliation-page__search'>
          <svg
            className='bx-reconciliation-page__search-icon'
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='11' cy='11' r='8' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
          <input
            type='search'
            aria-label='Filter reconciliation rows by station ID'
            placeholder='Filter by station ID'
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className='bx-reconciliation-page__actions'>
          <div className='bx-rec-filter-dropdown-wrapper'>
            <button
              type='button'
              className={`bx-btn-outline ${isFilterOpen ? 'active' : ''}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <img src={filterIcon} alt='' />
              <span>Filters</span>
            </button>

            {isFilterOpen && (
              <div className='bx-rec-filter-menu'>
                <div className='bx-rec-filter-menu__title'>Filter by Product</div>
                <button
                  type='button'
                  className={`bx-rec-filter-menu__item ${productFilter === 'all' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('all')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  All Products
                </button>
                <button
                  type='button'
                  className={`bx-rec-filter-menu__item ${productFilter === 'PMS' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('PMS')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  PMS
                </button>
                <button
                  type='button'
                  className={`bx-rec-filter-menu__item ${productFilter === 'AGO' ? 'selected' : ''}`}
                  onClick={() => {
                    setProductFilter('AGO')
                    setPage(1)
                    setIsFilterOpen(false)
                  }}
                >
                  AGO
                </button>
              </div>
            )}
          </div>

          <button
            type='button'
            className='bx-btn-outline'
            onClick={exportCsv}
            disabled={isLoading || !items.length}
          >
            <img src={exportIcon} alt='' />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Reconciliation Table */}
      <div className='bx-reconciliation-page__table-wrapper'>
        <table className='bx-reconciliation-table'>
          <thead>
            <tr>
              <th>Station</th>
              <th>Product</th>
              <th>Report date</th>
              <th>Pump sales</th>
              <th>Tank movement</th>
              <th>Variance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className='bx-rec-empty'>
                  Loading reconciliation data...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className='bx-rec-empty'>
                  No reconciliation records found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className='bx-rec-col-station'>{item.station}</td>
                  <td>{item.product}</td>
                  <td className='bx-rec-col-date'>{item.reportDate}</td>
                  <td className='bx-rec-col-pumps'>{item.pumpSales.toLocaleString()} L</td>
                  <td className='bx-rec-col-tank'>{item.tankMovement.toLocaleString()} L</td>
                  <td className='bx-rec-col-variance'>{renderVariance(item.variance)}</td>
                  <td className='bx-rec-col-status'>{renderStatusBadge(item.status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination matching Figma */}
      <div className='bx-reconciliation-page__pagination'>
        <div className='bx-reconciliation-page__pagination-controls'>
          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Previous
          </button>
          <div className='bx-reconciliation-page__pagination-numbers'>
            {getPaginationItems().map((item, idx) =>
              item === '...' ? (
                <span key={`dots-${idx}`} className='bx-pagination-dots'>
                  ...
                </span>
              ) : (
                <button
                  key={`page-${item}`}
                  type='button'
                  className={`bx-pagination-num ${page === item ? 'active' : ''}`}
                  onClick={() => setPage(Number(item))}
                >
                  {item}
                </button>
              ),
            )}
          </div>
          <button
            type='button'
            className='bx-pagination-nav'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}

export default Reconciliation
